import React, { useEffect, useMemo, useRef, useState } from "react";
import { useToast } from "@chakra-ui/react";
import {
  FiCheckCircle, FiClock, FiHome, FiXCircle, FiCalendar,
  FiCoffee, FiPauseCircle, FiWifi, FiWifiOff, FiLogIn, FiLogOut,
} from "react-icons/fi";
import { api } from "../../lib/api";
import { useShellHeader } from "../../Components/AppShell";
import { Card, CardHeader, Pill, EmptyState, Skeleton, Field, ATTENDANCE_TONES } from "../../Components/ui";

// Statuses that mean the person worked that day, so a mark needs a check-in time.
const WORKED_STATUSES = ["present", "late", "half-day", "wfh"];

// Each attendance status gets a colour + icon. Used by <StatusBadge />.
// Synthetic keys used only by the day-grid renderer:
//   "not_checked_in" → today, no check-in yet (neutral grey, not red)
//   "leave:<type>"   → on approved leave; shows "On Sick Leave" etc.
//
// Labels and icons stay here; colour comes from the shared tone table so these
// pills follow the theme (Components/ui/statusTones.js). This map was nine
// hardcoded light-theme triples, duplicated verbatim in the other attendance
// page. The old "weekend" pair (#94A3B8 on #F8FAFC) was 2.45:1 — an AA failure.
const STATUS_META = {
  present:        { label: "Present",        ...ATTENDANCE_TONES.present,        Icon: FiCheckCircle },
  late:           { label: "Late",           ...ATTENDANCE_TONES.late,           Icon: FiClock },
  wfh:            { label: "WFH",            ...ATTENDANCE_TONES.wfh,            Icon: FiHome },
  absent:         { label: "Absent",         ...ATTENDANCE_TONES.absent,         Icon: FiXCircle },
  holiday:        { label: "Holiday",        ...ATTENDANCE_TONES.holiday,        Icon: FiCoffee },
  leave:          { label: "On Leave",       ...ATTENDANCE_TONES.leave,          Icon: FiCalendar },
  "half-day":     { label: "Half Day",       ...ATTENDANCE_TONES["half-day"],    Icon: FiPauseCircle },
  not_checked_in: { label: "Not checked in", ...ATTENDANCE_TONES.not_checked_in, Icon: FiClock },
  weekend:        { label: "Weekend",        ...ATTENDANCE_TONES.weekend,        Icon: FiCoffee },
};

// Weekly off days for the org's single Mon–Fri shift: Sat (6) + Sun (0).
// dateStr is "YYYY-MM-DD"; parsed as UTC midnight so getUTCDay is stable.
function isWeekend(dateStr) {
  const day = new Date(dateStr + "T00:00:00Z").getUTCDay();
  return day === 0 || day === 6;
}
const LEAVE_TYPE_LABEL = {
  annual: "Annual", sick: "Sick", casual: "Casual", unpaid: "Unpaid",
  marriage: "Marriage", bereavement: "Bereavement", examination: "Examination",
  paternity: "Paternity", maternity: "Maternity", half_day: "Half Day",
};

// Status badge — coloured pill with leading icon. Use for the Status column.
// Optional `leaveType` adds the type to a "leave" status (e.g. "On Sick Leave").
function StatusBadge({ status, leaveType, size = "sm" }) {
  const m = STATUS_META[status] || STATUS_META.absent;
  const Icon = m.Icon;
  const pad = size === "lg" ? "4px 10px" : "2px 8px";
  const fz  = size === "lg" ? 12 : 11;
  const label = (status === "leave" && leaveType)
    ? `On ${LEAVE_TYPE_LABEL[leaveType] || leaveType} Leave`
    : m.label;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: pad, borderRadius: 999,
      background: m.bg, color: m.fg,
      border: `1px solid ${m.border}`,
      fontSize: fz, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
      whiteSpace: "nowrap",
    }}>
      <Icon size={fz + 2} strokeWidth={2.5} />
      {label}
    </span>
  );
}

// Decide what status badge to show for a row in the day grid. Priority:
//   1. explicit attendance record → use its status (+ note for leave type)
//   2. approved leave for the day → "leave" + leaveType
//   3. approved WFH for the day → "wfh"
//   4. date is today or future → "not_checked_in" (neutral grey)
//   5. date is in the past → "absent" (red)
function gridStatusFor(row, dateStr) {
  if (row.record) {
    const rec = row.record;
    // Check-in wins, and WFH is a location attribute: an office-verified check-in
    // means present in office, even on a WFH-approved day. Never show it as "WFH".
    if (rec.status === "wfh" && rec.wifiVerified) return { status: "present" };
    return { status: rec.status, leaveType: rec.leaveType };
  }
  if (row.onLeave) return { status: "leave", leaveType: row.onLeave };
  if (row.wfh)     return { status: "wfh" };
  // Sat/Sun are non-working days — never count them as absent.
  if (isWeekend(dateStr)) return { status: "weekend" };
  const today = new Date().toISOString().slice(0, 10);
  if (dateStr >= today) return { status: "not_checked_in" };
  return { status: "absent" };
}

// WiFi badge — distinguishes 3 states: office-WiFi check-in (green ✓),
// off-network check-in (amber, typically WFH-approved), and "not checked
// in yet" (muted dash). Only the first means "on company WiFi".
function WifiBadge({ rec }) {
  if (!rec || !rec.checkIn) {
    return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  }
  if (rec.wifiVerified) {
    return (
      <span title="Checked in from office Wi-Fi" style={{
        display: "inline-flex", alignItems: "center", gap: 5,
        padding: "2px 8px", borderRadius: 999,
        background: "var(--tone-success-bg)", color: "var(--tone-success-fg)",
        border: "1px solid var(--tone-success-br)",
        fontSize: 11, fontWeight: 700,
      }}>
        <FiWifi size={13} strokeWidth={2.5} /> Office
      </span>
    );
  }
  return (
    <span title="Checked in from outside office Wi-Fi" style={{
      display: "inline-flex", alignItems: "center", gap: 5,
      padding: "2px 8px", borderRadius: 999,
      background: "var(--tone-warning-bg)", color: "var(--tone-warning-fg)",
      border: "1px solid var(--tone-warning-br)",
      fontSize: 11, fontWeight: 700,
    }}>
      <FiWifiOff size={13} strokeWidth={2.5} /> Off-network
    </span>
  );
}

// Time cell with leading clock icon — improves scannability in the day grid.
function TimeCell({ time, kind }) {
  if (!time) return <span style={{ color: "var(--text-tertiary)" }}>—</span>;
  const Icon = kind === "out" ? FiLogOut : FiLogIn;
  const [h, m] = time.split(":").map(Number);
  const d = new Date(); d.setHours(h, m, 0, 0);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "var(--text-primary)", fontVariantNumeric: "tabular-nums" }}>
      <Icon size={12} style={{ opacity: 0.55 }} />
      {d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
    </span>
  );
}

const inputStyle = { border: "1px solid var(--border-default)", borderRadius: 8, padding: "6px 10px", fontSize: 13, background: "var(--bg-surface)", color: "var(--text-primary)" };
const btnPrimary = { background: "var(--accent-primary)", color: "white", padding: "8px 16px", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer" };
const btnGhost = { background: "transparent", color: "var(--text-secondary)", padding: "6px 12px", border: "1px solid var(--border-default)", borderRadius: 8, fontSize: 13, cursor: "pointer" };
// The hero is a fixed dark panel in both themes — the same treatment as the
// dashboard hero (Pages/Dashboard/EmployeeDashboard.jsx), so the two check-in
// surfaces read as one product. Labels on it are measured against black.
const HERO_BG = [
  "radial-gradient(120% 150% at 88% -10%, rgba(227,30,36,0.45) 0%, rgba(227,30,36,0.12) 40%, rgba(227,30,36,0) 72%)",
  "linear-gradient(135deg, #0A0A0B 0%, #000000 100%)",
].join(", ");

const btnDanger = { background: "transparent", color: "var(--tone-danger-fg)", padding: "4px 8px", border: "none", fontSize: 12, cursor: "pointer" };
const thStyle = { textAlign: "left", padding: "10px 12px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-tertiary)", background: "var(--bg-subtle)", borderBottom: "1px solid var(--border-subtle)" };
const tdBase = { padding: "10px 12px", borderTop: "1px solid var(--border-subtle)", color: "var(--text-secondary)" };

function fmtTime(t) {
  if (!t) return "—";
  const [h, m] = t.split(":").map(Number);
  const dt = new Date(); dt.setHours(h, m, 0, 0);
  return dt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}
function fmtDate(d) { return new Date(d).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); }

export default function AttendancePage() {
  // Admin-only view: SUPER_ADMIN doesn't have personal attendance — they
  // manage the team. The page renders ONLY the admin grid (month + day
  // view of staff check-ins). WFH inbox lives under /wfh, Settings under
  // /settings — keeping each concern on its own sidebar route.
  useShellHeader({ title: "Attendance" }, []);
  return <AdminGridPanel />;
}

// ============================================================
// EMPLOYEE DASHBOARD — port of plugin renderDashboard()
// ============================================================
function DashboardPanel() {
  const toast = useToast();
  const [today, setToday] = useState(null);
  const [dash, setDash] = useState(null);
  const [busy, setBusy] = useState(false);
  const [clock, setClock] = useState(new Date());

  // live clock
  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  async function load() {
    try {
      const [t, d] = await Promise.all([api("/attendance/today"), api("/attendance/dashboard")]);
      setToday(t); setDash(d);
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function checkin() {
    setBusy(true);
    try {
      const r = await api("/attendance/checkin", { method: "POST" });
      toast({ title: r.message || "Checked in", status: "success" });
      load();
    } catch (e) {
      const ipNote = e.data?.clientIp
        ? ` (We detected your network IP as ${e.data.clientIp}. If you're in the office, turn off mobile data and reconnect to the office WiFi, then try again.)`
        : "";
      toast({
        title: "Check-in failed",
        description: (e.data?.message || e.message || "Please refresh and try again.") + ipNote,
        status: "error",
        duration: 9000,
        isClosable: true,
      });
    } finally { setBusy(false); }
  }
  async function checkout() {
    setBusy(true);
    try {
      const r = await api("/attendance/checkout", { method: "POST" });
      toast({ title: r.message || "Checked out", status: "success" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setBusy(false); }
  }

  if (!today || !dash) return <Skeleton.Card />;

  const r = today.record;
  const hasIn = !!r?.checkIn;
  const hasOut = !!r?.checkOut;
  const wifiOk = today.isOfficeIp;
  const wifiUnconfigured = today.noIpsConfigured;
  const dateStr = clock.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  const timeStr = clock.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" });

  let hoursWorked = null;
  if (hasIn && hasOut) {
    const [ih, im] = r.checkIn.split(":").map(Number);
    const [oh, om] = r.checkOut.split(":").map(Number);
    hoursWorked = (((oh * 60 + om) - (ih * 60 + im)) / 60).toFixed(1);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* CHECK-IN WIDGET (faithful to plugin's dfhr-checkin-widget) */}
      <div style={{
        background: HERO_BG,
        color: "#FFFFFF", borderRadius: 14, padding: 24,
        boxShadow: "var(--shadow-lg)",
        display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24, flexWrap: "wrap",
      }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.72)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{dateStr}</div>
          <div className="tabular" style={{ fontSize: 42, fontWeight: 700, marginTop: 6, marginBottom: 8, letterSpacing: "-0.02em" }}>{timeStr}</div>
          <div style={{ fontSize: 14, color: "rgba(255,255,255,0.86)" }}>
            {hasIn && hasOut && (
              <>✅ Done for today · In: <b>{fmtTime(r.checkIn)}</b> · Out: <b>{fmtTime(r.checkOut)}</b> · <b>{hoursWorked}h worked</b></>
            )}
            {hasIn && !hasOut && (
              <>🟢 Checked in at <b>{fmtTime(r.checkIn)}</b>
                {r.status === "late" && <span style={{ color: "#FCD34D", marginLeft: 8 }}>(Late)</span>}
                {r.status === "wfh" && <span style={{ color: "#C4B5FD", marginLeft: 8 }}>(WFH)</span>}
              </>
            )}
            {!hasIn && <>You have not checked in today.</>}
          </div>

          {/* WiFi pill */}
          <div style={{ marginTop: 12 }}>
            {wifiUnconfigured ? (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 8, background: "rgba(245,158,11,0.15)", color: "#FCD34D", fontSize: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: 9999, background: "#FCD34D" }} /> WiFi rules not configured yet
              </div>
            ) : wifiOk ? (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 8, background: "rgba(16,185,129,0.15)", color: "#6EE7B7", fontSize: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: 9999, background: "#6EE7B7" }} /> On {today.wifiLabel}
              </div>
            ) : (
              <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "6px 12px", borderRadius: 8, background: "rgba(239,68,68,0.15)", color: "#FCA5A5", fontSize: 12 }}>
                <span style={{ width: 8, height: 8, borderRadius: 9999, background: "#FCA5A5" }} /> Not on {today.wifiLabel}
              </div>
            )}
            {today.wfhToday && (
              <div style={{ marginTop: 8, padding: "8px 12px", background: "rgba(139,92,246,0.15)", color: "#DDD6FE", borderRadius: 8, fontSize: 12 }}>
                🏠 Approved WFH day — you can check in from any network.
              </div>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: 12 }}>
          <button
            disabled={hasIn || busy}
            onClick={checkin}
            style={{
              padding: "14px 28px", fontSize: 15, fontWeight: 600, borderRadius: 10, border: "none",
              cursor: hasIn ? "not-allowed" : "pointer",
              background: hasIn ? "rgba(255,255,255,0.1)" : "var(--fill-success)",
              color: hasIn ? "rgba(255,255,255,0.72)" : "white",
              boxShadow: hasIn ? "none" : "0 4px 14px rgba(16,185,129,0.4)",
              opacity: busy && !hasIn ? 0.6 : 1,
            }}
          >
            {hasIn ? "✓ Checked In" : (busy ? "..." : "Check In")}
          </button>
          <button
            disabled={!hasIn || hasOut || busy}
            onClick={checkout}
            style={{
              padding: "14px 28px", fontSize: 15, fontWeight: 600, borderRadius: 10, border: "none",
              cursor: (!hasIn || hasOut) ? "not-allowed" : "pointer",
              background: hasOut ? "rgba(255,255,255,0.1)" : !hasIn ? "rgba(255,255,255,0.05)" : "var(--fill-danger)",
              color: hasOut || !hasIn ? "rgba(255,255,255,0.72)" : "white",
              boxShadow: (!hasIn || hasOut) ? "none" : "0 4px 14px rgba(239,68,68,0.4)",
              opacity: busy && hasIn && !hasOut ? 0.6 : 1,
            }}
          >
            {hasOut ? "✓ Checked Out" : (busy ? "..." : "Check Out")}
          </button>
        </div>
      </div>

      {/* QUOTE BANNER */}
      {dash.quoteOfTheDay && (
        <div style={{
          display: "flex", gap: 12, alignItems: "center",
          background: "linear-gradient(135deg, rgba(91,122,255,0.08) 0%, rgba(139,92,246,0.08) 100%)",
          border: "1px solid var(--border-subtle)", borderRadius: 12, padding: 16,
        }}>
          <div style={{ fontSize: 24 }}>💬</div>
          <div>
            <div style={{ fontSize: 14, color: "var(--text-primary)", fontStyle: "italic" }}>"{dash.quoteOfTheDay.q}"</div>
            <div style={{ fontSize: 12, color: "var(--text-tertiary)", marginTop: 4 }}>— {dash.quoteOfTheDay.a}</div>
          </div>
        </div>
      )}

      {/* GREETING */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 600, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
            Good day, {dash.staff.firstName} 👋
          </div>
          <div style={{ fontSize: 13, color: "var(--text-tertiary)", marginTop: 2 }}>
            {dash.staff.designation || "Team Member"}
            {dash.staff.department && ` · ${dash.staff.department}`}
          </div>
        </div>
      </div>

      {/* 6 STAT CARDS (port of plugin .dfhr-p-stats) */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
        <StatTile color="var(--fill-success)" label="Present this month" value={dash.attMonth.present} />
        <StatTile color="var(--fill-danger)" label="Absent"             value={dash.attMonth.absent}  />
        <StatTile color="var(--fill-warning)" label="Late Days"          value={dash.attMonth.late}    />
        <StatTile color="var(--fill-violet)" label="WFH Days"           value={dash.attMonth.wfh}     />
        <StatTile color="var(--fill-warning)" label="Leave Pending"      value={dash.leavesPending}    />
        <StatTile color="var(--fill-success)" label="Leave Approved"     value={dash.leavesApproved}   />
      </div>

      {/* TEAM STATUS — Out Today + WFH Today */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
        <Card padding="lg">
          <CardHeader title="❌ Out Today" />
          {dash.absentToday.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text-tertiary)", textAlign: "center", padding: 20 }}>🎉 Everyone is in today!</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {dash.absentToday.map((m, i) => (
                <PersonRow key={i} firstName={m.firstName} lastName={m.lastName} subtitle={m.designation || m.department} pill="On Leave" pillColor="var(--tone-info-fg)" pillBg="var(--tone-info-bg)" />
              ))}
            </div>
          )}
        </Card>

        <Card padding="lg">
          <CardHeader title="🏠 Working From Home" />
          {dash.wfhToday.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text-tertiary)", textAlign: "center", padding: 20 }}>No one on WFH today.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {dash.wfhToday.map((m, i) => (
                <PersonRow key={i} firstName={m.firstName} lastName={m.lastName} subtitle={m.department} pill="🏠 WFH" pillColor="var(--tone-violet-fg)" pillBg="var(--tone-violet-bg)" wfh />
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* HOLIDAYS + NOTICES */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
        <Card padding="lg">
          <CardHeader title="📅 Upcoming Holidays" />
          {dash.upcomingHolidays.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text-tertiary)", textAlign: "center", padding: 16 }}>No upcoming holidays.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {dash.upcomingHolidays.map((h, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderTop: i ? "1px solid var(--border-subtle)" : "none", fontSize: 13 }}>
                  <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{h.title}</span>
                  <span className="tabular" style={{ color: "var(--text-tertiary)" }}>{fmtDate(h.holidayDate)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card padding="lg">
          <CardHeader title="📢 Recent Notices" />
          {dash.recentNotices.length === 0 ? (
            <div style={{ fontSize: 13, color: "var(--text-tertiary)", textAlign: "center", padding: 16 }}>No notices.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {dash.recentNotices.map((n, i) => (
                <div key={i} style={{ padding: "10px 0", borderTop: i ? "1px solid var(--border-subtle)" : "none" }}>
                  <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text-primary)" }}>{n.title}</div>
                  <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{new Date(n.createdAt).toLocaleString()}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}

function StatTile({ color, label, value }) {
  return (
    <div style={{
      background: "var(--bg-surface)", border: "1px solid var(--border-subtle)",
      borderRadius: 12, padding: 14, position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: color }} />
      <div className="tabular" style={{ fontSize: 28, fontWeight: 700, color: "var(--text-primary)", letterSpacing: "-0.02em", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 6, fontWeight: 600 }}>{label}</div>
    </div>
  );
}

function PersonRow({ firstName, lastName, subtitle, pill, pillColor, pillBg, wfh }) {
  const initials = `${(firstName || "?").charAt(0)}${(lastName || "").charAt(0)}`.toUpperCase();
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 9999, fontSize: 13, fontWeight: 600,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: wfh ? "var(--fill-violet)" : "var(--accent-primary)",
        color: "white",
      }}>{initials}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text-primary)" }}>{`${firstName} ${lastName}`.trim()}</div>
        <div style={{ fontSize: 11, color: "var(--text-tertiary)" }}>{subtitle || "—"}</div>
      </div>
      <span style={{ fontSize: 11, padding: "3px 9px", borderRadius: 9999, background: pillBg, color: pillColor, fontWeight: 600 }}>{pill}</span>
    </div>
  );
}

// ============================================================
// HISTORY (unchanged)
// ============================================================
function HistoryPanel() {
  const toast = useToast();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState(() => {
    const t = new Date(); const f = new Date(t.getTime() - 30 * 86400000);
    return { from: f.toISOString().slice(0, 10), to: t.toISOString().slice(0, 10) };
  });

  async function load() {
    setLoading(true);
    try { const d = await api(`/attendance/my?from=${range.from}&to=${range.to}`); setRecords(d.records || []); }
    catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range.from, range.to]);

  return (
    <Card padding="none">
      <div style={{ padding: 14, borderBottom: "1px solid var(--border-subtle)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>My attendance</div>
        <div style={{ display: "flex", gap: 8 }}>
          <input type="date" style={inputStyle} value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
          <input type="date" style={inputStyle} value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
        </div>
      </div>
      {loading ? <div style={{ padding: 16 }}><Skeleton.Row count={4} /></div>
      : records.length === 0 ? <EmptyState icon="📅" title="No records in this window" />
      : (
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={thStyle}>Date</th><th style={thStyle}>Day</th><th style={thStyle}>Status</th>
            <th style={thStyle}>In</th><th style={thStyle}>Out</th><th style={thStyle}>Hours</th>
          </tr></thead>
          <tbody>
            {records.map((r) => {
              let hours = "—";
              if (r.checkIn && r.checkOut) {
                const [ih, im] = r.checkIn.split(":").map(Number);
                const [oh, om] = r.checkOut.split(":").map(Number);
                hours = `${((oh * 60 + om - ih * 60 - im) / 60).toFixed(1)}h`;
              }
              return (
                <tr key={r._id}>
                  <td className="tabular" style={{ ...tdBase, color: "var(--text-primary)" }}>{fmtDate(r.workDate)}</td>
                  <td style={tdBase}>{new Date(r.workDate).toLocaleDateString(undefined, { weekday: "short" })}</td>
                  <td style={tdBase}><StatusBadge status={r.status} /></td>
                  <td style={tdBase}><TimeCell time={r.checkIn}  kind="in"  /></td>
                  <td style={tdBase}><TimeCell time={r.checkOut} kind="out" /></td>
                  <td className="tabular" style={{ ...tdBase, color: "var(--text-primary)" }}>{hours}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ============================================================
// WFH (employee submit + my requests)
// ============================================================
function WfhPanel() {
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ fromDate: "", toDate: "", reason: "" });

  async function load() {
    setLoading(true);
    try { const d = await api("/attendance/wfh/my"); setRequests(d.wfh || []); }
    catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  async function submit() {
    if (!form.fromDate || !form.toDate) { toast({ title: "Pick both dates", status: "warning" }); return; }
    try {
      await api("/attendance/wfh", { method: "POST", body: JSON.stringify(form) });
      toast({ title: "WFH request submitted", status: "success" });
      setForm({ fromDate: "", toDate: "", reason: "" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }
  async function cancel(id) {
    if (!window.confirm("Cancel this WFH request?")) return;
    try { await api(`/attendance/wfh/${id}`, { method: "DELETE" }); load(); }
    catch (e) { toast({ title: e.message, status: "error" }); }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card padding="lg">
        <CardHeader title="🏠 Request Work From Home" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 2fr auto", gap: 10, alignItems: "end" }}>
          <Field label="From"><input type="date" style={{ ...inputStyle, width: "100%" }} value={form.fromDate} onChange={(e) => setForm({ ...form, fromDate: e.target.value })} /></Field>
          <Field label="To"><input type="date" style={{ ...inputStyle, width: "100%" }} value={form.toDate} onChange={(e) => setForm({ ...form, toDate: e.target.value })} /></Field>
          <Field label="Reason"><input style={{ ...inputStyle, width: "100%" }} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why are you working from home?" /></Field>
          <button style={btnPrimary} onClick={submit}>Submit</button>
        </div>
        <div style={{ marginTop: 10, padding: 10, background: "rgba(139,92,246,0.08)", borderRadius: 8, fontSize: 12, color: "var(--text-secondary)" }}>
          ⚡ WFH must be approved before the date. On an approved WFH day, you can check in from any network and it will be recorded as WFH.
        </div>
      </Card>

      <Card padding="none">
        <div style={{ padding: 14, borderBottom: "1px solid var(--border-subtle)", fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>My requests</div>
        {loading ? <div style={{ padding: 16 }}><Skeleton.Row count={3} /></div>
        : requests.length === 0 ? <EmptyState icon="🏠" title="No WFH requests yet" />
        : (
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead><tr>
              <th style={thStyle}>From</th><th style={thStyle}>To</th>
              <th style={thStyle}>Reason</th><th style={thStyle}>Status</th>
              <th style={thStyle}>Admin note</th>
              <th style={{ ...thStyle, textAlign: "right" }}></th>
            </tr></thead>
            <tbody>
              {requests.map((w) => (
                <tr key={w._id}>
                  <td className="tabular" style={{ ...tdBase, color: "var(--text-primary)" }}>{fmtDate(w.fromDate)}</td>
                  <td className="tabular" style={tdBase}>{fmtDate(w.toDate)}</td>
                  <td style={tdBase}>{w.reason || "—"}</td>
                  <td style={tdBase}><Pill tone={w.status === "approved" ? "success" : w.status === "rejected" ? "danger" : w.status === "cancelled" ? "neutral" : "warning"} size="sm">{w.status}</Pill></td>
                  <td style={tdBase}>{w.adminNote || "—"}</td>
                  <td style={{ ...tdBase, textAlign: "right" }}>
                    {w.status === "pending" && <button style={btnGhost} onClick={() => cancel(w._id)}>Cancel</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ============================================================
// ADMIN GRID — port of admin/views/attendance.php (Month + Day toggle)
// ============================================================
function AdminGridPanel() {
  const toast = useToast();
  const [view, setView] = useState("day"); // month | day  — Day View is the default landing
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [date, setDate]   = useState(new Date().toISOString().slice(0, 10));
  const [monthData, setMonthData] = useState(null);
  const [dayRows, setDayRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [editing, setEditing] = useState(null); // attendance entry or {} for new
  const [editForm, setEditForm] = useState({ staffId: "", workDate: "", checkIn: "", checkOut: "", status: "present", note: "", reason: "" });
  const checkInRef = useRef(null);
  const checkOutRef = useRef(null);

  async function loadStaff() {
    try { const d = await api("/hr/staff"); setStaffList(d.staff || []); } catch (e) {}
  }
  useEffect(() => { loadStaff(); }, []);

  async function loadMonth() {
    setLoading(true);
    try { setMonthData(await api(`/attendance/month?month=${month}`)); }
    catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  async function loadDay() {
    setLoading(true);
    try { const d = await api(`/attendance/grid?date=${date}`); setDayRows(d.rows || []); }
    catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { if (view === "month") loadMonth(); /* eslint-disable-next-line */ }, [view, month]);
  useEffect(() => { if (view === "day") loadDay(); /* eslint-disable-next-line */ }, [view, date]);

  function openNew(forDate) {
    setEditing({});
    setEditForm({ staffId: "", workDate: forDate || date, checkIn: "", checkOut: "", status: "present", note: "", reason: "" });
  }
  function openEdit(row) {
    setEditing(row);
    setEditForm({
      _id: row._id,
      staffId: row.staff,
      workDate: new Date(row.workDate).toISOString().slice(0, 10),
      checkIn: row.checkIn || "",
      checkOut: row.checkOut || "",
      status: row.status,
      note: row.note || "",
      reason: "",
    });
  }
  async function saveEdit() {
    const reason = (editForm.reason || "").trim();
    if (!reason) { toast({ title: "A reason is required for this correction.", status: "warning" }); return; }
    // A half-filled time box (say the hour typed but no AM/PM) reads as empty,
    // so the mark used to save with no check-in and the day still showed
    // "not checked in" (Oct 2026). Refuse those outright.
    for (const [ref, label] of [[checkInRef, "Check-in"], [checkOutRef, "Check-out"]]) {
      if (ref.current && ref.current.validity && ref.current.validity.badInput) {
        toast({ title: `${label} time is incomplete`, description: "Fill in the hour, minutes and AM/PM, or clear the box.", status: "warning" });
        return;
      }
    }
    if (WORKED_STATUSES.includes(editForm.status) && !editForm.checkIn) {
      const ok = window.confirm("No check-in time is set. This day will show as not checked in, and late and hours calculations will have nothing to work from.\n\nSave without a check-in time?");
      if (!ok) return;
    }
    try {
      if (editForm._id) {
        await api(`/attendance/${editForm._id}`, { method: "PATCH", body: JSON.stringify({
          reason,
          checkIn: editForm.checkIn || null, checkOut: editForm.checkOut || null,
          status: editForm.status, note: editForm.note,
        })});
      } else {
        if (!editForm.staffId) { toast({ title: "Pick a staff member", status: "warning" }); return; }
        await api("/attendance/admin/mark", { method: "POST", body: JSON.stringify({
          reason,
          staffId: editForm.staffId, workDate: editForm.workDate, status: editForm.status,
          checkIn: editForm.checkIn || null, checkOut: editForm.checkOut || null, note: editForm.note,
        })});
      }
      toast({ title: "Saved", status: "success" });
      setEditing(null);
      view === "month" ? loadMonth() : loadDay();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }
  async function deleteEntry(id) {
    if (!window.confirm("Delete this attendance entry?")) return;
    try { await api(`/attendance/${id}`, { method: "DELETE" }); loadDay(); }
    catch (e) { toast({ title: e.message, status: "error" }); }
  }
  function exportCsv() {
    const tok = localStorage.getItem("token");
    const url = `${window.location.origin}/api/reports/attendance?from=${month}-01&to=${month}-31`;
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } }).then((r) => r.blob()).then((b) => {
      const u = URL.createObjectURL(b);
      const a = document.createElement("a"); a.href = u; a.download = `attendance_${month}.csv`; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    }).catch((e) => toast({ title: "Export failed", status: "error" }));
  }
  // WP parity: dfhr_export_attendance_excel → /api/exports/attendance.xlsx?month=YYYY-MM
  function exportXlsx() {
    const tok = localStorage.getItem("token");
    const url = `${window.location.origin}/api/exports/attendance.xlsx?month=${encodeURIComponent(month)}`;
    fetch(url, { headers: { Authorization: `Bearer ${tok}` } }).then((r) => r.blob()).then((b) => {
      const u = URL.createObjectURL(b);
      const a = document.createElement("a"); a.href = u; a.download = `Attendance_${month}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(u), 1000);
    }).catch(() => toast({ title: "Export failed", status: "error" }));
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* VIEW TOGGLE */}
      <div style={{ display: "inline-flex", border: "1px solid var(--border-subtle)", borderRadius: 10, overflow: "hidden", alignSelf: "flex-start" }}>
        <button onClick={() => setView("month")} style={{ padding: "8px 16px", fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer",
          background: view === "month" ? "var(--accent-primary)" : "var(--bg-surface)",
          color: view === "month" ? "white" : "var(--text-secondary)" }}>📅 Month View</button>
        <button onClick={() => setView("day")} style={{ padding: "8px 16px", fontSize: 13, fontWeight: 500, border: "none", cursor: "pointer",
          background: view === "day" ? "var(--accent-primary)" : "var(--bg-surface)",
          color: view === "day" ? "white" : "var(--text-secondary)" }}>📆 Day View</button>
      </div>

      {/* CONTROLS */}
      {view === "month" ? (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--text-tertiary)" }}>Month:</span>
          <input type="month" style={inputStyle} value={month} onChange={(e) => setMonth(e.target.value)} />
          <button style={btnPrimary} onClick={loadMonth}>Load</button>
          <button style={btnGhost} onClick={exportCsv}>⬇ Export CSV</button>
          <button style={btnGhost} onClick={exportXlsx}>📊 Export Excel</button>
          <div style={{ flex: 1 }} />
          <button style={btnGhost} onClick={() => openNew()}>+ Mark Attendance</button>
        </div>
      ) : (
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 13, color: "var(--text-tertiary)" }}>Date:</span>
          <input type="date" style={inputStyle} value={date} onChange={(e) => setDate(e.target.value)} />
          <button style={btnPrimary} onClick={loadDay}>Load Day</button>
          <div style={{ flex: 1 }} />
          <button style={btnGhost} onClick={() => openNew(date)}>+ Mark Attendance</button>
        </div>
      )}

      {/* SUMMARY PILLS */}
      {view === "month" && monthData?.totals && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <SummaryPill label="Present" value={monthData.totals.present} color="var(--fill-success)" />
          <SummaryPill label="Late" value={monthData.totals.late} color="var(--fill-warning)" />
          <SummaryPill label="Half Day" value={monthData.totals.halfDay} color="var(--fill-orange)" />
          <SummaryPill label="WFH" value={monthData.totals.wfh} color="var(--fill-violet)" />
          <SummaryPill label="Leave" value={monthData.totals.leave} color="var(--fill-info)" />
          <SummaryPill label="Absent" value={monthData.totals.absent} color="var(--fill-danger)" />
        </div>
      )}

      {/* TABLES */}
      <Card padding="none">
        {loading ? <div style={{ padding: 16 }}><Skeleton.Row count={6} /></div>
        : view === "month" ? (
          !monthData || monthData.rows.length === 0 ? (
            <EmptyState icon="👥" title="No staff" description="Click Load to fetch." />
          ) : (
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Staff</th>
                <th style={thStyle}>Department</th>
                <th style={{ ...thStyle, color: "var(--tone-success-fg)", textAlign: "center" }}>Present</th>
                <th style={{ ...thStyle, color: "var(--tone-warning-fg)", textAlign: "center" }}>Late</th>
                <th style={{ ...thStyle, color: "var(--tone-orange-fg)", textAlign: "center" }}>Half Day</th>
                <th style={{ ...thStyle, color: "var(--tone-violet-fg)", textAlign: "center" }}>WFH</th>
                <th style={{ ...thStyle, color: "var(--tone-info-fg)", textAlign: "center" }}>Leave</th>
                <th style={{ ...thStyle, color: "var(--tone-danger-fg)", textAlign: "center" }}>Absent</th>
              </tr></thead>
              <tbody>
                {monthData.rows.map((r, i) => (
                  <tr key={r.user._id}>
                    <td style={{ ...tdBase, color: "var(--text-tertiary)" }}>{i + 1}</td>
                    <td style={{ ...tdBase, color: "var(--text-primary)" }}>
                      <div style={{ fontWeight: 500 }}>{r.user.name}</div>
                      <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{r.user.employeeCode || "—"}</div>
                    </td>
                    <td style={tdBase}>{r.user.department || "—"}</td>
                    <td className="tabular" style={{ ...tdBase, color: "var(--tone-success-fg)", textAlign: "center", fontWeight: 600 }}>{r.counts.present}</td>
                    <td className="tabular" style={{ ...tdBase, color: "var(--tone-warning-fg)", textAlign: "center", fontWeight: 600 }}>{r.counts.late}</td>
                    <td className="tabular" style={{ ...tdBase, color: "var(--tone-orange-fg)", textAlign: "center", fontWeight: 600 }}>{r.counts.halfDay}</td>
                    <td className="tabular" style={{ ...tdBase, color: "var(--tone-violet-fg)", textAlign: "center", fontWeight: 600 }}>{r.counts.wfh}</td>
                    <td className="tabular" style={{ ...tdBase, color: "var(--tone-info-fg)", textAlign: "center", fontWeight: 600 }}>{r.counts.leave}</td>
                    <td className="tabular" style={{ ...tdBase, color: "var(--tone-danger-fg)", textAlign: "center", fontWeight: 600 }}>{r.counts.absent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          dayRows.length === 0 ? <EmptyState icon="📅" title="Select a date and click Load Day" /> : (
            <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
              <thead><tr>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Staff</th>
                <th style={thStyle}>Department</th>
                <th style={thStyle}>Check In</th>
                <th style={thStyle}>Check Out</th>
                <th style={thStyle}>Status</th>
                <th style={thStyle}>Late</th>
                <th style={thStyle}>WiFi ✓</th>
                <th style={thStyle}>Note</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Actions</th>
              </tr></thead>
              <tbody>
                {dayRows.map((r, i) => {
                  const rec = r.record;
                  const { status, leaveType } = gridStatusFor(r, date);
                  return (
                    <tr key={r.user._id}>
                      <td style={{ ...tdBase, color: "var(--text-tertiary)" }}>{i + 1}</td>
                      <td style={{ ...tdBase, color: "var(--text-primary)" }}>
                        <div style={{ fontWeight: 500 }}>{r.user.name}</div>
                        <div style={{ fontSize: 10, color: "var(--text-tertiary)" }}>{r.user.employeeCode || "—"}</div>
                      </td>
                      <td style={tdBase}>{r.user.team?.name || "—"}</td>
                      <td style={tdBase}><TimeCell time={rec?.checkIn}  kind="in"  /></td>
                      <td style={tdBase}><TimeCell time={rec?.checkOut} kind="out" /></td>
                      {/* A late check-in is still "present" — show PRESENT here and flag
                          the lateness in the dedicated Late column to its right. */}
                      <td style={tdBase}><StatusBadge status={status === "late" ? "present" : status} leaveType={leaveType} /></td>
                      <td style={tdBase}>
                        {status === "late" ? (
                          <span style={{ background: "var(--tone-warning-bg)", color: "var(--tone-warning-fg)", padding: "2px 10px", borderRadius: 12, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>⏰ Late</span>
                        ) : <span style={{ color: "var(--text-tertiary)" }}>—</span>}
                      </td>
                      <td style={tdBase}><WifiBadge rec={rec} /></td>
                      <td style={{ ...tdBase, fontSize: 11 }}>{rec?.note || "—"}</td>
                      <td style={{ ...tdBase, textAlign: "right" }}>
                        {rec ? (
                          <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                            <button style={btnGhost} onClick={() => openEdit({ ...rec, staff: r.user._id })}>Edit</button>
                            <button style={btnDanger} onClick={() => deleteEntry(rec._id)}>Delete</button>
                          </div>
                        ) : (
                          <button style={btnGhost} onClick={() => { setEditing({}); setEditForm({ staffId: r.user._id, workDate: date, checkIn: "", checkOut: "", status: "present", note: "" }); }}>Mark</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}
      </Card>

      {/* MARK / EDIT MODAL */}
      {editing && (
        <div onClick={() => setEditing(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(4px)" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--bg-surface)", borderRadius: 14, padding: 20, width: 480, maxWidth: "90vw", boxShadow: "var(--shadow-xl)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={{ fontSize: 16, fontWeight: 600, color: "var(--text-primary)" }}>{editForm._id ? "Edit Attendance" : "Mark Attendance"}</div>
              <button onClick={() => setEditing(null)} style={{ background: "transparent", border: "none", fontSize: 22, color: "var(--text-tertiary)", cursor: "pointer" }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {!editForm._id && (
                <Field label="Staff *">
                  <select style={{ ...inputStyle, width: "100%" }} value={editForm.staffId} onChange={(e) => setEditForm({ ...editForm, staffId: e.target.value })}>
                    <option value="">— Select —</option>
                    {staffList.map((u) => <option key={u._id} value={u._id}>{u.firstName || u.lastName ? `${u.firstName} ${u.lastName}`.trim() : u.username} ({u.email})</option>)}
                  </select>
                </Field>
              )}
              <Field label="Date *">
                <input type="date" disabled={!!editForm._id} style={{ ...inputStyle, width: "100%" }} value={editForm.workDate} onChange={(e) => setEditForm({ ...editForm, workDate: e.target.value })} />
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="Check In"><input ref={checkInRef} type="time" style={{ ...inputStyle, width: "100%" }} value={editForm.checkIn || ""} onChange={(e) => setEditForm({ ...editForm, checkIn: e.target.value })} /></Field>
                <Field label="Check Out"><input ref={checkOutRef} type="time" style={{ ...inputStyle, width: "100%" }} value={editForm.checkOut || ""} onChange={(e) => setEditForm({ ...editForm, checkOut: e.target.value })} /></Field>
              </div>
              {WORKED_STATUSES.includes(editForm.status) && !editForm.checkIn && (
                <div role="note" style={{ fontSize: 12.5, lineHeight: 1.45, padding: "8px 10px", borderRadius: 8, background: "var(--tone-warning-bg)", color: "var(--tone-warning-fg)" }}>
                  No check-in time yet. Without one, this day shows as not checked in.
                </div>
              )}
              <Field label="Status">
                <select style={{ ...inputStyle, width: "100%" }} value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}>
                  <option value="present">Present</option>
                  <option value="late">Late</option>
                  <option value="half-day">Half Day</option>
                  <option value="wfh">WFH</option>
                  <option value="leave">Leave</option>
                  <option value="absent">Absent</option>
                  <option value="holiday">Holiday</option>
                </select>
              </Field>
              <Field label="Note">
                <textarea style={{ ...inputStyle, width: "100%", minHeight: 60, resize: "vertical" }} value={editForm.note} onChange={(e) => setEditForm({ ...editForm, note: e.target.value })} />
              </Field>
              <Field label="Reason (required)">
                <textarea
                  style={{ ...inputStyle, width: "100%", minHeight: 60, resize: "vertical" }}
                  value={editForm.reason}
                  onChange={(e) => setEditForm({ ...editForm, reason: e.target.value })}
                  placeholder="Why is this record being corrected? (recorded in the audit log)"
                />
              </Field>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              <button style={btnGhost} onClick={() => setEditing(null)}>Cancel</button>
              <button style={btnPrimary} onClick={saveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryPill({ label, value, color }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 14px", borderRadius: 9999,
      background: "var(--bg-surface)", border: "1px solid var(--border-subtle)",
    }}>
      <span style={{ width: 8, height: 8, borderRadius: 9999, background: color }} />
      <span style={{ fontSize: 12, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600 }}>{label}</span>
      <span className="tabular" style={{ fontSize: 14, color: "var(--text-primary)", fontWeight: 700 }}>{value}</span>
    </div>
  );
}

// ============================================================
// WFH INBOX (unchanged)
// ============================================================
function WfhInboxPanel() {
  const toast = useToast();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");

  async function load() {
    setLoading(true);
    try { const d = await api(`/attendance/wfh${filter ? `?status=${filter}` : ""}`); setRequests(d.wfh || []); }
    catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  async function decide(id, decision) {
    const note = decision === "rejected" ? window.prompt("Rejection reason (optional):") || "" : "";
    try {
      await api(`/attendance/wfh/${id}/decision`, { method: "PATCH", body: JSON.stringify({ decision, adminNote: note }) });
      toast({ title: decision === "approved" ? "Approved" : "Rejected", status: "success" });
      load();
    } catch (e) { toast({ title: e.message, status: "error" }); }
  }

  return (
    <Card padding="none">
      <div style={{ padding: 14, borderBottom: "1px solid var(--border-subtle)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>WFH inbox</div>
        <select style={inputStyle} value={filter} onChange={(e) => setFilter(e.target.value)}>
          <option value="">All</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      {loading ? <div style={{ padding: 16 }}><Skeleton.Row count={3} /></div>
      : requests.length === 0 ? <EmptyState icon="📥" title="Nothing to review" />
      : (
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead><tr>
            <th style={thStyle}>Employee</th><th style={thStyle}>From</th><th style={thStyle}>To</th>
            <th style={thStyle}>Reason</th><th style={thStyle}>Status</th>
            <th style={{ ...thStyle, textAlign: "right" }}></th>
          </tr></thead>
          <tbody>
            {requests.map((w) => (
              <tr key={w._id}>
                <td style={{ ...tdBase, color: "var(--text-primary)" }}>{w.staff?.firstName || w.staff?.lastName ? `${w.staff?.firstName} ${w.staff?.lastName}`.trim() : w.staff?.username}</td>
                <td className="tabular" style={tdBase}>{fmtDate(w.fromDate)}</td>
                <td className="tabular" style={tdBase}>{fmtDate(w.toDate)}</td>
                <td style={tdBase}>{w.reason || "—"}</td>
                <td style={tdBase}><Pill tone={w.status === "approved" ? "success" : w.status === "rejected" ? "danger" : w.status === "cancelled" ? "neutral" : "warning"} size="sm">{w.status}</Pill></td>
                <td style={{ ...tdBase, textAlign: "right" }}>
                  {w.status === "pending" && (
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button style={btnGhost} onClick={() => decide(w._id, "rejected")}>Reject</button>
                      <button style={btnPrimary} onClick={() => decide(w._id, "approved")}>Approve</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ============================================================
// SETTINGS (unchanged)
// ============================================================
function SettingsPanel() {
  const toast = useToast();
  const [settings, setSettings] = useState(null);
  const [ipsText, setIpsText] = useState("");
  const [saving, setSaving] = useState(false);

  async function load() {
    try { const d = await api("/attendance/settings"); setSettings(d.settings); setIpsText((d.settings.officeIps || []).join("\n")); }
    catch (e) { toast({ title: e.message, status: "error" }); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      const ips = ipsText.split("\n").map((s) => s.trim()).filter(Boolean);
      const d = await api("/attendance/settings", { method: "PUT", body: JSON.stringify({ ...settings, officeIps: ips }) });
      setSettings(d.settings);
      toast({ title: "Settings saved", status: "success" });
    } catch (e) { toast({ title: e.message, status: "error" }); }
    finally { setSaving(false); }
  }

  if (!settings) return <Skeleton.Card />;

  return (
    <Card padding="lg">
      <CardHeader title="Attendance settings" />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Field label="Strict Wi-Fi gating">
          <label style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 13 }}>
            <input type="checkbox" checked={settings.strictWifi} onChange={(e) => setSettings({ ...settings, strictWifi: e.target.checked })} />
            <span style={{ color: "var(--text-secondary)" }}>Block check-in when not on office network (unless WFH approved)</span>
          </label>
        </Field>
        <Field label="Wi-Fi label">
          <input style={{ ...inputStyle, width: "100%" }} value={settings.officeWifiLabel} onChange={(e) => setSettings({ ...settings, officeWifiLabel: e.target.value })} />
        </Field>
        <Field label="Late threshold (min after shift start)">
          <input type="number" min={0} max={120} style={{ ...inputStyle, width: "100%" }} value={settings.lateThreshold} onChange={(e) => setSettings({ ...settings, lateThreshold: parseInt(e.target.value, 10) || 0 })} />
        </Field>
        <Field label="Office IPs / CIDRs (one per line)" full>
          <textarea
            value={ipsText}
            onChange={(e) => setIpsText(e.target.value)}
            placeholder={`e.g.\n203.0.113.42\n10.0.0.0/24`}
            style={{ ...inputStyle, width: "100%", fontFamily: "monospace", minHeight: 100, resize: "vertical" }}
          />
        </Field>
      </div>
      <div style={{ marginTop: 16 }}>
        <button style={btnPrimary} disabled={saving} onClick={save}>{saving ? "Saving…" : "Save settings"}</button>
      </div>
    </Card>
  );
}

